import React, { useState, useRef } from 'react';
import { generateCharacterPortraitWithGemini, editImage } from '../services/geminiService';
import { generateImage, generateElementImage, generateCharacterPortrait, deleteClonedVoice } from '../services/aiService';
import { buildCharacterSheetPrompt } from '../utils/characterSheetPrompt';
import { fetchImageViaProxy, uploadDataUriToBackend, isAlreadyPersisted } from '../services/backendService';
import { UserAssetsModal } from './wizard/UserAssetsModal';
import { ActorsLibraryModal } from './wizard/ActorsLibraryModal';
import { ElevenLabsVoiceModal } from './ElevenLabsVoiceModal';
import { VoiceClonerModal } from './VoiceClonerModal';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { CREDIT_COSTS } from '../types';

export const CharacterPortraitModal = ({ character, projectData, onClose, onSave, onDeductCredits, onTrackCreditsUsed, onSaveMedia }) => {
    const [portraitImage, setPortraitImage] = useState(character.image || character.portraitImage || null);
    const [costumes, setCostumes] = useState(character.costumes || character.costumeAndAccessoryImages || []);
    const [name, setName] = useState(character.name || '');
    const [description, setDescription] = useState(character.description || '');
    const [editPrompt, setEditPrompt] = useState('');
    const [status, setStatus] = useState('idle'); // idle | generating | editing | generatingCostume
    const [error, setError] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');

    // Modal states for User Assets / Library pickers
    const [showAssetsFor, setShowAssetsFor] = useState(null); // 'portrait' | 'costume' | null
    const [showLibraryFor, setShowLibraryFor] = useState(null); // 'portrait' | 'costume' | null

    const portraitUploadRef = useRef(null);
    const costumeUploadRef = useRef(null);

    const isElement = character.category === 'element';
    const aspectRatio = projectData?.aspectRatio || '16:9';

    // Helper: persist a generated image (fal.ai URL or base64) to GCS
    const persistToGCS = async (imageUrl) => {
        if (!imageUrl) return imageUrl;
        // Fast path (Apr 2026): backend already persisted server-side; nothing to do
        if (isAlreadyPersisted(imageUrl)) return imageUrl;
        if (imageUrl.startsWith('data:')) {
            try {
                const userEmail = sessionStorage.getItem('userEmail');
                const gcsUrl = await uploadDataUriToBackend(imageUrl, userEmail);
                if (gcsUrl) return gcsUrl;
            } catch (e) { console.warn('⚠️ GCS upload failed:', e.message); }
            return imageUrl;
        }
        if (!imageUrl.startsWith('http')) return imageUrl;
        try {
            const base64 = await fetchImageViaProxy(imageUrl);
            if (!base64 || !base64.startsWith('data:')) return imageUrl;
            const userEmail = sessionStorage.getItem('userEmail');
            const gcsUrl = await uploadDataUriToBackend(base64, userEmail);
            return gcsUrl || base64;
        } catch (e) { console.warn('⚠️ Persist to GCS failed:', e.message); return imageUrl; }
    };

    // ─── Generate Portrait ───
    const handleGenerate = async () => {
        setStatus('generating'); setError(null); setStatusMsg('Generating portrait...');
        try {
            let imageUrl, creditsUsed;
            if (isElement) {
                const result = await generateElementImage(description || name, [], aspectRatio, 'default');
                imageUrl = result?.imageUrl || result?.images?.[0];
                creditsUsed = result?.creditsUsed;
            } else {
                // Args: (description, refImages, costumeImages, makeup, aspectRatio, category, modelId)
                // Generate a CHARACTER SHEET (front/3-quarter/profile/back + 4 close-ups)
                // rather than a single stand-alone portrait. Layout adapts to
                // project aspect ratio (landscape/square/portrait).
                const sheetPrompt = buildCharacterSheetPrompt(name, description, aspectRatio);
                const result = await generateCharacterPortraitWithGemini(
                    sheetPrompt,
                    [],
                    [],
                    'natural',
                    aspectRatio,
                    'character',
                    'default'
                );
                imageUrl = result?.image || result?.imageUrl || result?.images?.[0] || result;
                creditsUsed = result?.creditsUsed || CREDIT_COSTS.CHARACTER_PORTRAIT;
            }
            if (imageUrl) {
                setStatusMsg('Saving to cloud storage...');
                const gcsUrl = await persistToGCS(typeof imageUrl === 'object' ? imageUrl.image : imageUrl);
                setPortraitImage(gcsUrl);
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (onTrackCreditsUsed && creditsUsed) onTrackCreditsUsed(creditsUsed);
            }
        } catch (err) { setError(err.message || 'Generation failed'); }
        finally { setStatus('idle'); setStatusMsg(''); }
    };

    // ─── Edit Portrait with Prompt ───
    const handleEditWithPrompt = async () => {
        if (!portraitImage || !editPrompt.trim()) return;
        setStatus('editing'); setError(null); setStatusMsg('Editing image...');
        try {
            const result = await editImage(portraitImage, editPrompt, [], [], null, 'default', aspectRatio, setStatusMsg);
            const edited = result?.imageUrl;
            if (edited) {
                setStatusMsg('Saving to cloud storage...');
                const gcsUrl = await persistToGCS(edited);
                setPortraitImage(gcsUrl);
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (onTrackCreditsUsed && result.creditsUsed) onTrackCreditsUsed(result.creditsUsed);
                setEditPrompt('');
            }
        } catch (err) { setError(err.message || 'Edit failed'); }
        finally { setStatus('idle'); setStatusMsg(''); }
    };

    // Build a professional costume designer prompt from script + character context
    const buildCostumePrompt = () => {
        const script = projectData?.script || '';
        const scriptSnippet = script.length > 800 ? script.substring(0, 800) + '...' : script;
        const charName = name || 'the character';

        return [
            `You are a professional costume designer for a film or advertisement production.`,
            `Analyze the following story/script and character, then design a complete wardrobe look.\n`,
            `CHARACTER: "${charName}"`,
            description ? `DESCRIPTION: ${description}` : '',
            scriptSnippet ? `\nSCRIPT/STORY:\n${scriptSnippet}\n` : '',
            `Based on the story's genre, setting, time period, mood, and this character's role and personality, design a wardrobe that:`,
            `- Reflects the character's social status, occupation, and emotional arc`,
            `- Is appropriate for the era, culture, and location described in the story`,
            `- Uses fabrics, textures, colors, and patterns that convey the character's personality`,
            `- Includes footwear, jewelry, watches, bags, hats, scarves, or any accessories that complete the look`,
            `- Adds any story-relevant props (e.g., a detective's magnifying glass, a chef's apron, a soldier's dog tags)\n`,
            `OUTPUT: A single photorealistic image of the complete outfit and all accessories laid out flat on a clean neutral background (light grey or white).`,
            `Style: Professional fashion flat-lay photography, editorial quality, sharp detail, soft even lighting.`,
            `IMPORTANT: Show ONLY the clothing, shoes, and accessories — absolutely no human body, mannequin, or person wearing them.`,
        ].filter(Boolean).join('\n');
    };

    // ─── Generate Costume ───
    const handleGenerateCostume = async () => {
        setStatus('generatingCostume'); setError(null); setStatusMsg('Generating costume...');
        try {
            const prompt = buildCostumePrompt();
            const result = await generateImage(prompt, 1, 'default', '1:1', [], true, null);
            const url = result?.images?.[0] || result?.imageUrl;
            if (url) {
                setStatusMsg('Saving to cloud storage...');
                const gcsUrl = await persistToGCS(url);
                setCostumes(prev => [...prev, gcsUrl]);
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (onTrackCreditsUsed && result.creditsUsed) onTrackCreditsUsed(result.creditsUsed);
            }
        } catch (err) { setError(err.message || 'Costume generation failed'); }
        finally { setStatus('idle'); setStatusMsg(''); }
    };

    // ─── File Uploads ───
    const handlePortraitUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => { setPortraitImage(reader.result); if (onSaveMedia) onSaveMedia([reader.result]); };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCostumeUpload = (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => { setCostumes(prev => [...prev, reader.result]); if (onSaveMedia) onSaveMedia([reader.result]); };
            reader.readAsDataURL(file);
        });
        e.target.value = '';
    };

    // ─── Asset / Library Selection ───
    const handleAssetSelect = (imageUrl) => {
        if (showAssetsFor === 'portrait') setPortraitImage(imageUrl);
        else if (showAssetsFor === 'costume') setCostumes(prev => [...prev, imageUrl]);
        if (onSaveMedia) onSaveMedia([imageUrl]);
        setShowAssetsFor(null);
    };

    const [actorVoiceId, setActorVoiceId] = useState(character?.voiceId || character?.elevenLabsVoiceId || null);
    const [actorId, setActorId] = useState(character?.actorId || null);
    const [actorImageGallery, setActorImageGallery] = useState(character?.imageGallery || []);
    // Feb 2026 — BytePlus ModelArk (Seedance) pre-approved actor asset refs.
    // Seeded from the character's existing fields so a round-trip through
    // this modal doesn't drop them, and refreshed when the user picks a new
    // Library actor from inside this modal.
    const [byteplusAssetGroupId, setByteplusAssetGroupId] = useState(character?.byteplusAssetGroupId || null);
    const [byteplusAssetId, setByteplusAssetId] = useState(character?.byteplusAssetId || null);
    const [useByteplusForSeedance, setUseByteplusForSeedance] = useState(
        character?.useByteplusForSeedance === false
            ? false
            : Boolean(character?.byteplusAssetGroupId && character?.byteplusAssetId)
    );

    // Feb 2026 — ElevenLabs voice picker + cloner state.
    // `voiceName` is a human label shown in the UI (never sent to the LLM).
    // `voiceSource` distinguishes library picks from user-cloned voices so
    // we know whether to call DELETE /api/ai/voice/{id} on cleanup.
    const [voiceName, setVoiceName]       = useState(character?.voiceName || '');
    const [voiceSource, setVoiceSource]   = useState(character?.voiceSource || (character?.voiceId ? 'library' : null));
    const [showVoiceLibrary, setShowVoiceLibrary] = useState(false);
    const [showVoiceCloner, setShowVoiceCloner]   = useState(false);

    const handleLibrarySelect = (actorData) => {
        const url = actorData.imageUrl;
        if (!url) return;
        if (showLibraryFor === 'portrait') {
            setPortraitImage(url);
            if (actorData.name && !name.trim()) setName(actorData.name);
            if (actorData.description && !description.trim()) setDescription(actorData.description);
            // Store actor metadata (voiceId, actorId, gallery)
            if (actorData.voiceId) setActorVoiceId(actorData.voiceId);
            if (actorData.actorId) setActorId(actorData.actorId);
            if (actorData.imageGallery?.length) setActorImageGallery(actorData.imageGallery);
            // Feb 2026 — capture BytePlus ModelArk (Seedance) asset refs when
            // a Library actor is picked INSIDE the Character Editor. Without
            // this the modal would overwrite the previous actor's refs with
            // nothing, breaking downstream Seedance video generation.
            if (actorData.byteplusAssetGroupId !== undefined) {
                setByteplusAssetGroupId(actorData.byteplusAssetGroupId || null);
            }
            if (actorData.byteplusAssetId !== undefined) {
                setByteplusAssetId(actorData.byteplusAssetId || null);
            }
            if (actorData.useByteplusForSeedance !== undefined) {
                setUseByteplusForSeedance(Boolean(actorData.useByteplusForSeedance));
            }
        } else if (showLibraryFor === 'costume') {
            setCostumes(prev => [...prev, url]);
        }
        if (onSaveMedia) onSaveMedia([url]);
        setShowLibraryFor(null);
    };

    const removeCostume = (idx) => setCostumes(prev => prev.filter((_, i) => i !== idx));

    // ─── Virtual Try-On ───
    const handleTryOn = async () => {
        if (!portraitImage || costumes.length === 0) return;
        setStatus('generating');
        setStatusMsg('Generating virtual try-on...');
        setError(null);
        try {
            const tryOnPrompt = `Medium shot portrait of ${name || 'this character'}: ${description || ''}. Frame from waist up, clearly showing the character's face AND the full costume/outfit. The character MUST wear EXACTLY the outfit shown in the costume reference images. Ensure the face is sharp and recognizable, and the costume details (fabric, color, accessories) are fully visible. Natural pose, cinematic lighting.`;
            const result = await generateCharacterPortrait(
                tryOnPrompt,
                [portraitImage],
                costumes,
                '',
                aspectRatio,
                'character',
                'default'
            );
            const tryOnUrl = result?.image || result?.imageUrl || result?.images?.[0];
            if (tryOnUrl) {
                const gcsUrl = await persistToGCS(tryOnUrl);
                setPortraitImage(gcsUrl);
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (result?.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
            }
        } catch (err) {
            setError(`Try-on failed: ${err.message}`);
        } finally {
            setStatus('idle');
            setStatusMsg('');
        }
    };

    // ─── Voice handlers ───
    const handleVoicePicked = (voiceId, name) => {
        setActorVoiceId(voiceId);
        setVoiceName(name || 'ElevenLabs voice');
        setVoiceSource('library');
        setShowVoiceLibrary(false);
    };
    const handleVoiceCloned = ({ voiceId, voiceName: name }) => {
        setActorVoiceId(voiceId);
        setVoiceName(name || 'Cloned voice');
        setVoiceSource('cloned');
    };
    const handleRemoveVoice = async () => {
        // Only call DELETE for cloned voices — library voices are shared and
        // must NOT be deleted from ElevenLabs.
        if (voiceSource === 'cloned' && actorVoiceId) {
            const ok = window.confirm(`Delete cloned voice "${voiceName || actorVoiceId}"? This frees the ElevenLabs slot and cannot be undone.`);
            if (!ok) return;
            await deleteClonedVoice(actorVoiceId);
        }
        setActorVoiceId(null);
        setVoiceName('');
        setVoiceSource(null);
    };

    // ─── Save ───
    const handleSave = () => {
        const saveData = {
            name,
            description,
            image: portraitImage,
            category: character?.category || 'character',
            costumes,
            costumeAndAccessoryImages: costumes.length > 0 ? costumes : undefined,
            referenceImages: character.referenceImages,
        };
        // Preserve actor metadata (voice, actorId, gallery)
        if (actorVoiceId) {
            saveData.voiceId = actorVoiceId;
            saveData.elevenLabsVoiceId = actorVoiceId; // legacy alias used by Storyboard
        }
        if (voiceName)    saveData.voiceName   = voiceName;
        if (voiceSource)  saveData.voiceSource = voiceSource;
        if (actorId) saveData.actorId = actorId;
        if (actorImageGallery.length > 0) saveData.imageGallery = actorImageGallery;
        // Feb 2026 — persist BytePlus ModelArk (Seedance) asset refs so the
        // merge in Storyboard.onSave can forward them to video generation.
        // We write null explicitly when a Library pick clears them, otherwise
        // the stale previous actor's refs would survive the spread merge.
        saveData.byteplusAssetGroupId = byteplusAssetGroupId || null;
        saveData.byteplusAssetId = byteplusAssetId || null;
        saveData.useByteplusForSeedance = Boolean(useByteplusForSeedance);
        // Preserve any existing fields not managed by this modal
        if (character?.originalPortrait) saveData.originalPortrait = character.originalPortrait;

        onSave(character.id, saveData);
        onClose();
    };

    const isLoading = status !== 'idle';
    const btnBase = 'px-3 py-1.5 text-[11px] rounded-lg font-medium flex items-center gap-1.5 transition-colors disabled:opacity-40';
    const btnGray = `${btnBase} bg-gray-700 hover:bg-gray-600 text-white`;
    const btnPurple = `${btnBase} bg-purple-700 hover:bg-purple-600 text-white`;
    const btnGreen = `${btnBase} bg-emerald-700 hover:bg-emerald-600 text-white`;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="character-portrait-modal">
            <div className="bg-[#111119] rounded-2xl border border-gray-800 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800 shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-base font-bold text-white">{isElement ? 'Element Editor' : 'Character Editor'}</h2>
                        <p className="text-[11px] text-gray-500">Aspect Ratio: <span className="text-purple-400 font-semibold">{aspectRatio}</span></p>
                        {/* Feb 2026 — BytePlus ModelArk (Seedance) asset badge.
                            Visible when the character has a pre-approved asset
                            attached. Lets the user confirm the refs survived
                            the Cast & World → Studio Editor hop. */}
                        {(byteplusAssetGroupId && byteplusAssetId) && (
                            <div
                                className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[10px] font-mono text-amber-200 max-w-full"
                                title={`Seedance asset — Group: ${byteplusAssetGroupId}\nAsset: ${byteplusAssetId}${useByteplusForSeedance === false ? '\n(opted out)' : ''}`}
                                data-testid="seedance-asset-badge"
                            >
                                <span className="uppercase tracking-wider text-amber-300/80 shrink-0">Seedance</span>
                                <span className="truncate">{byteplusAssetId}</span>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white" data-testid="portrait-modal-close">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Error */}
                    {error && <div className="p-2.5 bg-red-900/30 border border-red-700/50 rounded-lg text-xs text-red-300">{error}</div>}
                    {/* Status */}
                    {isLoading && (
                        <div className="flex items-center gap-2 p-2.5 bg-purple-900/20 border border-purple-700/30 rounded-lg">
                            <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-xs text-purple-300">{statusMsg || 'Processing...'}</span>
                        </div>
                    )}

                    {/* ═══ SECTION 1: Character Name & Description ═══ */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Name</label>
                            <input value={name} onChange={e => setName(e.target.value)} placeholder="Character name"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:border-purple-500 focus:outline-none" data-testid="char-name-input" />
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Description</label>
                            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Character description"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:border-purple-500 focus:outline-none" data-testid="char-desc-input" />
                        </div>
                    </div>

                    {/* ═══ SECTION 2: Character Portrait ═══ */}
                    <div className="border border-gray-800 rounded-xl p-4 space-y-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Character Image</p>

                        {/* Portrait preview */}
                        {portraitImage ? (
                            <div className="relative w-48 h-48 mx-auto rounded-xl overflow-hidden border border-gray-700 bg-gray-900">
                                <img src={portraitImage} alt={name} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                            </div>
                        ) : (
                            <div className="w-48 h-48 mx-auto rounded-xl border border-dashed border-gray-700 flex items-center justify-center text-gray-600 text-xs">
                                No image
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 justify-center">
                            <input type="file" accept="image/*" className="hidden" ref={portraitUploadRef} onChange={handlePortraitUpload} />
                            <button onClick={handleGenerate} disabled={isLoading} className={btnGreen} data-testid="generate-portrait-btn">
                                <MagicWandIcon className="w-3.5 h-3.5" /> Generate
                            </button>
                            <button onClick={() => portraitUploadRef.current?.click()} disabled={isLoading} className={btnGray} data-testid="upload-portrait-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                                Upload
                            </button>
                            <button onClick={() => setShowAssetsFor('portrait')} disabled={isLoading} className={btnGray} data-testid="assets-portrait-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                                User Assets
                            </button>
                            <button onClick={() => setShowLibraryFor('portrait')} disabled={isLoading} className={btnPurple} data-testid="library-portrait-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                Assets Library
                            </button>
                        </div>
                    </div>

                    {/* ═══ SECTION 3: Edit Image with Prompts ═══ */}
                    {portraitImage && (
                        <div className="border border-gray-800 rounded-xl p-4 space-y-3">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Edit Image</p>
                            <div className="flex gap-2">
                                <textarea value={editPrompt} onChange={e => setEditPrompt(e.target.value)}
                                    placeholder="Describe how you want to edit this image..."
                                    className="flex-1 p-2.5 bg-gray-800 border border-gray-700 rounded-lg resize-none h-16 text-sm text-gray-200 placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                                    data-testid="edit-prompt-input" />
                                <button onClick={handleEditWithPrompt} disabled={isLoading || !editPrompt.trim()} className={`${btnGreen} self-end`} data-testid="apply-edit-btn">
                                    <MagicWandIcon className="w-3.5 h-3.5" /> Apply
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ═══ SECTION 4: Costume & Accessories ═══ */}
                    <div className="border border-gray-800 rounded-xl p-4 space-y-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Costume & Accessories</p>

                        {/* Costume thumbnails */}
                        {costumes.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {costumes.map((img, idx) => (
                                    <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-700 group/c">
                                        <img src={img} alt={`Costume ${idx+1}`} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                        <button onClick={() => removeCostume(idx)}
                                            className="absolute top-0 right-0 bg-black/70 hover:bg-red-600 rounded-bl w-5 h-5 flex items-center justify-center text-[9px] text-white opacity-0 group-hover/c:opacity-100 transition-opacity"
                                            data-testid={`remove-costume-${idx}`}>x</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Costume action buttons */}
                        <div className="flex flex-wrap gap-2">
                            <input type="file" accept="image/*" multiple className="hidden" ref={costumeUploadRef} onChange={handleCostumeUpload} />
                            <button onClick={handleGenerateCostume} disabled={isLoading} className={btnGreen} data-testid="generate-costume-btn">
                                <MagicWandIcon className="w-3.5 h-3.5" /> Generate
                            </button>
                            <button onClick={() => costumeUploadRef.current?.click()} disabled={isLoading} className={btnGray} data-testid="upload-costume-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                                Upload
                            </button>
                            <button onClick={() => setShowAssetsFor('costume')} disabled={isLoading} className={btnGray} data-testid="assets-costume-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                                User Assets
                            </button>
                            <button onClick={() => setShowLibraryFor('costume')} disabled={isLoading} className={btnPurple} data-testid="library-costume-btn">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                Assets Library
                            </button>
                            {portraitImage && costumes.length > 0 && (
                                <button onClick={handleTryOn} disabled={isLoading}
                                    className={`${btnBase} bg-amber-600 hover:bg-amber-500 text-white font-semibold`}
                                    data-testid="tryon-btn">
                                    {status === 'generating' && statusMsg.includes('try-on') ? (
                                        <><div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Trying On...</>
                                    ) : (
                                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg> Try On</>
                                    )}
                                </button>
                            )}
                        </div>

                        {costumes.length === 0 && (
                            <p className="text-[10px] text-gray-600">No costumes added. Add costume reference images for visual consistency in scene generation.</p>
                        )}
                    </div>

                    {/* ═══ SECTION 5: Voice (ElevenLabs) — Feb 2026 ═══ */}
                    {!isElement && (
                        <div className="border border-gray-800 rounded-xl p-4 space-y-3" data-testid="voice-picker-section">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Voice</p>

                            {actorVoiceId ? (
                                <div className="flex items-center justify-between gap-3 p-2.5 bg-gray-800/60 border border-gray-700 rounded-lg">
                                    <div className="min-w-0">
                                        <div className="text-sm text-gray-200 truncate" data-testid="voice-current-name">
                                            {voiceName || 'ElevenLabs voice'}
                                        </div>
                                        <div className="text-[10px] text-gray-500 flex items-center gap-2">
                                            <span className={`px-1.5 py-0.5 rounded-full ${voiceSource === 'cloned' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-violet-500/20 text-violet-300'}`}>
                                                {voiceSource === 'cloned' ? 'Cloned' : 'Library'}
                                            </span>
                                            <span className="font-mono truncate" title={actorVoiceId}>{actorVoiceId}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0">
                                        <button
                                            onClick={() => setShowVoiceLibrary(true)}
                                            className={btnGray}
                                            data-testid="voice-change-btn"
                                        >
                                            Change
                                        </button>
                                        <button
                                            onClick={handleRemoveVoice}
                                            className={`${btnBase} bg-red-700 hover:bg-red-600 text-white`}
                                            data-testid="voice-remove-btn"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => setShowVoiceLibrary(true)}
                                        className={btnPurple}
                                        data-testid="voice-pick-library-btn"
                                    >
                                        Pick from Library
                                    </button>
                                    <button
                                        onClick={() => setShowVoiceCloner(true)}
                                        className={btnGreen}
                                        data-testid="voice-clone-btn"
                                    >
                                        Clone My Voice
                                    </button>
                                    <p className="text-[10px] text-gray-600 w-full">
                                        Pick from the ElevenLabs library (free) or clone a 15-60s recording of your own voice (200 credits).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-800 shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="portrait-cancel-btn">Cancel</button>
                    <button onClick={handleSave} disabled={!portraitImage && costumes.length === 0}
                        className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold rounded-lg transition-colors"
                        data-testid="portrait-save-btn">
                        Save Changes
                    </button>
                </div>
            </div>

            {/* User Assets Modal */}
            {showAssetsFor && (
                <UserAssetsModal
                    activeTab={showAssetsFor === 'portrait' ? 'characters' : 'elements'}
                    onSelect={handleAssetSelect}
                    onClose={() => setShowAssetsFor(null)}
                />
            )}

            {/* Assets Library Modal */}
            {showLibraryFor && (
                <ActorsLibraryModal
                    onSelect={handleLibrarySelect}
                    onClose={() => setShowLibraryFor(null)}
                />
            )}

            {/* Feb 2026 — Voice Library (ElevenLabs) */}
            {showVoiceLibrary && (
                <ElevenLabsVoiceModal
                    onSelect={handleVoicePicked}
                    onClose={() => setShowVoiceLibrary(false)}
                />
            )}

            {/* Feb 2026 — Voice Cloner (record / upload → clone) */}
            {showVoiceCloner && (
                <VoiceClonerModal
                    voiceNameSuggested={name ? `${name} voice` : ''}
                    onCloned={handleVoiceCloned}
                    onClose={() => setShowVoiceCloner(false)}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                />
            )}
        </div>
    );
};
